import { isNumericLike } from '@orgbrain/lib-data';
import { capitalizeCompany } from '@orgbrain/lib-util';
import moment from 'moment';
import { v4 as uuid } from 'uuid';
import {
  ContractStatus,
  Entity,
  ShareClass,
  ShareholderOnboardPayload,
  StockContract,
  StockContractType,
} from '../../classes';
import { Entities } from '../../model/entity';
import {
  defaultHeaders,
  defaultHeadersEnum,
  defaultShareClass,
  GenericData,
  headerMaps,
  OrganizationTypeForOwners,
  OrgNrDobLenght,
  translatedStatus,
  translatedType,
} from '../generic-data';
import { validDateFormats } from '../valid-dates';

export function mapHeaderToDefault(header: string) {
  if (!header) throw new Error(' Empty header.');

  const trimmedHeader = header.toLowerCase().trim().replace(/ /g, '');
  if (defaultHeaders.find((defaultHeader) => trimmedHeader === defaultHeader)) {
    return trimmedHeader;
  }

  for (const key of Object.keys(headerMaps)) {
    if (headerMaps[key].find((headerMapHeader: string) => headerMapHeader === trimmedHeader)) {
      return key;
    }
  }
  return header;
}

export function detectNonValidHeaders(headers: string[]) {
  return headers
    .filter((header) => {
      const trimmedHeader = header.toLowerCase().trim().replace(/ /g, '');
      if (defaultHeaders.find((defaultHeader) => trimmedHeader === defaultHeader)) {
        return false;
      }

      for (const key of Object.keys(headerMaps)) {
        if (headerMaps[key].find((headerMapHeader: string) => headerMapHeader === trimmedHeader)) {
          return false;
        }
      }
      return true;
    })
    .map((header) => {
      return `"${header}"`;
    });
}

export const requiredContactFields = ['firstname', 'lastname', 'email'];
export const requiredAlternativeContactFields = ['name', 'email'];
export const optionalContactFields = ['address', 'type', 'phone'];

// Determine if this is Real additional share class row.   TODO rename
export function isVinillaRow(row: GenericData): boolean {
  if (row.companyname || row.firstname || row.lastname || row.orgnr) return true;
  return false;
}

// Create names expected for share classes in the data.
// Merge multiple rows (after master row) into single row with all the shares
export function flattenAdditionalShareRowsIntoSingleEntityRow(data: GenericData[]): GenericData[] {
  const newData = [];

  let masterRow;
  for (const row of data) {
    if (isVinillaRow(row)) {
      masterRow = row;
      newData.push(masterRow);
    }
    if (row._amount) {
      masterRow[`shareclass_${row._shareclass}`] = row._amount;
    }
    if (row._notes) {
      masterRow[`notes_${row._shareclass}`] = row._notes;
    }
  }

  return newData;
}

// Create share names in correct from.
// Merge multiple rows into single row with all the shares.
// We expect full rows so we need to merge duplicate owners
export function processShareRowsFullDataAnyWhere(data: GenericData[]): GenericData[] {
  const newData = [];

  const lookup: { [key: string]: GenericData } = {};

  for (const row of data) {
    const isCompany = !!row.orgnr;
    let key;
    if (isCompany) {
      key = row.orgnr;
    } else {
      key = row.firstname + row.lastname + row.dob;
    }

    lookup[key] = lookup[key] || row;
    lookup[key][`shareclass_${row._shareclass}`] = row._amount;
    lookup[key][`notes_${row._shareclass}`] = row._notes;
  }

  for (const key of Object.keys(lookup)) {
    newData.push(lookup[key]);
  }

  return newData;
}

// is this row just for additional contact ?
export function isAdditionalContactRow(row: GenericData) {
  let requiredCnt = 0;
  for (const key of Object.keys(row)) {
    if (row[key]) {
      if (
        requiredContactFields.find((name) => name === key) ||
        requiredAlternativeContactFields.find((name) => name === key)
      ) {
        requiredCnt++;
      } else if (optionalContactFields.find((name) => name === key)) {
        console.log('optionalContactFields', key);
      } else {
        return false;
      }
    }
  }
  if (
    requiredCnt === requiredContactFields.length ||
    requiredCnt === requiredAlternativeContactFields.length
  ) {
    return true;
  }

  return false;
}

//  Determine if identities match
export function doEntitiesMatch(existing: any, newEntity: Entity | any): Entity | null {
  const aName =
    (newEntity.firstName || '').toLowerCase().trim() +
    ' ' +
    (newEntity.lastName || '').toLowerCase().trim();
  if (newEntity.orgnr && existing[newEntity.orgnr]) {
    return existing[newEntity.orgnr];
  } else if (
    newEntity.email &&
    existing[newEntity.email.toLowerCase().trim()] &&
    !existing[newEntity.email.toLowerCase().trim()].hasDuplicateEmails &&
    !newEntity.hasDuplicateEmails &&
    !newEntity.orgnr
  ) {
    return existing[newEntity.email.toLowerCase().trim()];
  } else if (
    newEntity.firstName &&
    newEntity.lastName &&
    existing[aName] &&
    !existing[aName].hasDuplicateNames &&
    !newEntity.hasDuplicateNames &&
    !newEntity.orgnr
  ) {
    return existing[aName];
  } else if (newEntity.dob && existing[generateDateOfBirth(newEntity.dob)]) {
    let x = existing[generateDateOfBirth(newEntity.dob)];
    if (newEntity.ssn && x.ssn && x.ssn !== newEntity.ssn) {
      return null;
    } else {
      return existing[generateDateOfBirth(newEntity.dob)];
    }
  } else {
    return null;
  }
}

//  Do generic rows have same entity
export function rowMatches(a: GenericData, b: GenericData) {
  if (a.orgnr && a.orgnr === b.orgnr) return true;
  if (
    !(a.orgnr || b.orgnr) &&
    a.firstname === b.firstname &&
    a.lastname === b.lastname &&
    a.dob === b.dob
  )
    return true;
  return false;
}

export function hasRequiredHeaders(row: string[]) {
  if (row.length < 1) return false;
  const map = {};

  for (const key of row) {
    map[key] = true;
  }

  if (map[defaultHeadersEnum.email]) {
    return true;
  }

  return false;
}

export function typeFromHeaders(row: string[]): OrganizationTypeForOwners {
  if (row.find((x) => x.toLowerCase().trim() === 'membername'))
    return OrganizationTypeForOwners.SCHOOL;
  if (row.find((x) => x.toLowerCase().trim() === 'companyname'))
    return OrganizationTypeForOwners.COMPANY;

  return null;
}

export function isEmptyRow(row: GenericData): boolean {
  return (
    !(row.firstname && row.lastname && !row.extraContacts) &&
    !(row.companyname && row.orgnr) &&
    !row.email
  );
}

function fillEmptyRow(row: any, previousRow: any) {
  if (previousRow.orgnr && previousRow.companyname) {
    row.orgnr = previousRow.orgnr;
    row.companyname = previousRow.companyname;
    row.email = previousRow.email;
  }
  row.firstname = previousRow.firstname;
  row.lastname = previousRow.lastname;
  row.email = previousRow.email;
  row.dob = previousRow.dob;
  if (row.pnr) {
    row.pnr = previousRow.pnr;
  }
  return row;
}

function fillEmptyRows(data: any) {
  let previousRow = data[0];

  const YesNo = ['yes', 'ja', 'y', 'j'];

  for (let row of data) {
    if (row.extraContacts) {
      // if the row is an extra contact row check the list if the field is yes or no
      if (YesNo.includes(row.extraContacts.toLowerCase())) {
        row.extraContacts = true;
      } else {
        delete row.extraContacts;
      }
    }
    if (row.contractTitle) {
      row.isContractField = true;
    }
    if (isEmptyRow(row)) {
      row = fillEmptyRow(row, previousRow);
    } else {
      previousRow = row;
    }
  }

  return data;
}

function isAdditionalContactSimple(row: any) {
  return (!row._amount && !row.contractTitle) || row.extraContacts;
}
//check if the row is a contract row
function isAdditionalContractSimple(row: any) {
  return !row._amount && (row.first_name || row.orgnr) && row.contractTitle;
}

function createAdditionalContactsField(data: GenericData[]) {
  let previousRow = data[0];

  for (const row of data) {
    if (isAdditionalContractSimple(row)) {
      continue;
    }
    if (!previousRow.additionalContacts && isAdditionalContactSimple(row)) {
      previousRow['additionalContacts'] = [row];
      row['isAdditional'] = true;
    } else if (previousRow.additionalContacts && isAdditionalContactSimple(row)) {
      previousRow['additionalContacts'].push(row);
      row['isAdditional'] = true;
    } else {
      previousRow = row;
    }
  }
  return data;
}

function createContractsField(data: GenericData[]) {
  let previousRow = data[0];
  let first = true;
  for (const row of data) {
    if (row.additionalContracts) {
      first = false;

      if (row.additionalContracts.length > 0) {
        const rowWithoutContract = { ...row };
        delete rowWithoutContract?.additionalContracts;
        row['additionalContracts'].push(rowWithoutContract);
      }
      previousRow = row;
    } else if (!previousRow.additionalContracts && isAdditionalContractSimple(row)) {
      //
      first = false;

      previousRow['additionalContracts'] = [row];
      row['isContract'] = true;
    } else if (previousRow.additionalContracts && isAdditionalContractSimple(row)) {
      //
      first = false;

      previousRow['additionalContracts'].push(row);
      row['isContract'] = true;
    } else {
      const uniqe = createUniqueKeyForEntity(row);
      const prevousUniqe = createUniqueKeyForEntity(previousRow);

      if (uniqe === prevousUniqe && !first) {
        previousRow['additionalContracts'] = [...previousRow['additionalContracts'], row];
        row['isContract'] = true;
      } else {
        first = false;
        if (row.contractTitle) {
          const rowWithoutContract = { ...row };
          delete rowWithoutContract?.additionalContracts;
          row['additionalContracts'] = [rowWithoutContract];
        }
        previousRow = row;
      }
    }
  }
  return data;
}
function removeAdditionalContactsRows(data: GenericData[]) {
  return data.filter((row) => !row['isAdditional']);
}
function removeContractRows(data: GenericData[]) {
  return data.filter((row) => !row['isContract']);
}

export function createUniqueKeyForEntityAndShareclass(data: GenericData): string {
  if (data.companyname) {
    return `${data.companyname} ${data.orgnr} ${data._shareclass} `;
  } else if (data.email && !data.extraContacts) {
    return `${data.email} ${data._shareclass}${data.dob ? ' ' + data.dob : ''}${
      data.pnr ? ' ' + data.pnr : ''
    }${data.firstname ? ' ' + data.firstname : ''}${data.lastname ? ' ' + data.lastname : ''}`;
  } else if (data.firstname && data.lastname) {
    return `${data.firstname.toLowerCase()} ${data.lastname.toLowerCase()} ${data._shareclass}${
      data.dob ? ' ' + data.dob : ''
    }${data.pnr ? ' ' + data.pnr : ''}`;
  } else {
    return '';
  }
}

export function createUniqueKeyForEntity(data: GenericData): string {
  if (data.companyname) {
    return `${data.companyname} ${data.orgnr}`;
  } else if (data.email) {
    return `${data.email}${data.dob ? ' ' + data.dob : ''}${
      data.firstname ? ' ' + data.firstname : ''
    }${data.lastname ? ' ' + data.lastname : ''}`;
  } else if (!data.email) {
    return `${data.firstname.toLowerCase()} ${data.lastname.toLowerCase()}${
      data.dob ? ' ' + data.dob : ''
    }`;
  } else {
    return '';
  }
}

function generateEntityLookup(data, keyGeneratorFunction, usenomenee = true) {
  const entityLookup: { [key: string]: GenericData[] } = {};
  const keyused: { [key: string]: number } = {};

  for (const item of data) {
    let key = keyGeneratorFunction(item);
    if (entityLookup[key]) {
      if (item.accounttype && usenomenee) {
        if (
          item.accounttype.toLowerCase() === 'nominee' ||
          item.accounttype.toLowerCase() === 'megler'
        ) {
          item.orgnr = generateNewOrgNr(item, key, keyused);
          key = keyGeneratorFunction(item);
          keyused[key] = 1;
          entityLookup[key] = [item];
        } else {
          entityLookup[key].push(item);
        }
      } else {
        entityLookup[key].push(item);
      }
    } else {
      entityLookup[key] = [item];
    }
  }

  return entityLookup;
}
function generateNewOrgNr(item, key, keyused) {
  //if we have a used a key before, we need to generate the next keynumber with the orgnumber
  if (keyused[key]) {
    keyused[key] = keyused[key] + 1;
  } else {
    keyused[key] = 1;
  }
  return item.orgnr + '-' + keyused[key];
}
function createShareclassRecordsField(data, orgtype: string) {
  const newData: GenericData[] = [];
  const entityLookup = generateEntityLookup(data, createUniqueKeyForEntity, false);
  const keys = Object.keys(entityLookup);

  for (const key of keys) {
    const rows = entityLookup[key];

    const newRow = rows[0];

    newRow['records'] = [
      {
        _shareclass: newRow._shareclass
          ? newRow._shareclass
          : defaultShareClass[orgtype] || defaultShareClass['AS'],
        _amount: newRow._amount,
        _notes: newRow._notes,
      },
    ];
    delete newRow._shareclass;
    delete newRow._notes;
    delete newRow._amount;

    for (const row of rows.slice(1)) {
      newRow['records'].push({
        _shareclass: row._shareclass,
        _amount: row._amount,
        _notes: row._notes,
      });
    }
    newData.push(newRow);
  }

  return newData;
}

export function mergeDuplicateEntityAndShareclassRows(data: GenericData[]) {
  const newData: GenericData[] = [];
  const duplicateRows = [];

  const entityLookup = generateEntityLookup(data, createUniqueKeyForEntityAndShareclass);

  const keys = Object.keys(entityLookup);
  for (const key of keys) {
    const rows = entityLookup[key];
    const newRow = rows[0];
    const rowStringlist = [];
    let isFirst = true;
    const contracts = [];
    if (rows.length !== 1) {
      // for each duplicate row,
      rows.forEach((row) => {
        // merge the data exept for the shareamount and sharenotes into a string
        //do not need to look at amount or notes since they are most likely different
        const { _amount, _notes, _linenumber, ...rest } = row;
        //check for contract rows if row is contract row do not add that as duplicate
        if (!row.isContractField || isFirst) {
          isFirst = false;
          //take out any fields that are not in the generic data
          for (const key of Object.keys(rest)) {
            if (!defaultHeaders.includes(key)) {
              delete rest[key];
            }
          }
          const rowString = JSON.stringify(rest);
          // if the string is not already in the duplicateRows array, add it
          if (!rowStringlist.includes(rowString)) {
            rowStringlist.push(rowString);
          }
        }
      });

      if (rowStringlist.length > 1) {
        duplicateRows.push(rows);
      }
    }

    for (const item of rows.slice(1)) {
      if (item.isContractField) {
        contracts.push(item);
      }
      newRow._amount += +item._amount;

      newRow._notes += ', ' + item._notes;
    }
    if (contracts.length > 0) {
      newRow.additionalContracts = contracts;
    }

    newData.push(newRow);
  }

  return { payload: newData, duplicateRows };
}

export function isCorrectDateFormat(dob) {
  return moment(dob, validDateFormats, true).isValid();
}

export function generateDateOfBirth(item) {
  if (isCorrectDateFormat(item.dob)) {
    const date = moment(item.dob, validDateFormats, true);
    const ds = date.format('DDMMYY');
    if (ds !== 'Invalid Date') return ds;
    else return item.dob;
  } else if (!item.dob) {
    return null;
  } else {
    if (item.dob.length === 5 && isNumericLike(item.dob)) {
      return '0' + item.dob;
    }
    return item.dob;
  }
}

function markDuplicateNameEntities(entities: Entity[]): Entity[] {
  const entityMap = entities.reduce((acc: any, entity: Entity): any => {
    const name: string = Entities.getPersonEntityName(entity);
    if (acc[name] && !entity.orgnr) {
      acc[name] = acc[name] + 1;
    } else if (!entity.orgnr) {
      acc[name] = 1;
    }

    return acc;
  }, {});

  const newEntities = entities.map((entity: Entity): Entity | any => {
    const name: string = Entities.getPersonEntityName(entity);
    if (entityMap[name] > 1 && !entity.orgnr) {
      return { ...entity, hasDuplicateNames: true };
    }
    return entity;
  });

  return newEntities;
}

function markDuplicateEmailEntities(entities: Entity[]): Entity[] {
  const entityMap = entities.reduce((acc: any, entity: Entity): any => {
    const email: string = Entities.getEntityEmail(entity);
    if (acc[email] && !entity.orgnr) {
      acc[email] = acc[email] + 1;
    } else if (!entity.orgnr) {
      acc[email] = 1;
    }

    return acc;
  }, {});

  const newEntities = entities.map((entity: Entity): Entity | any => {
    const email: string = Entities.getEntityEmail(entity);

    if (entityMap[email] > 1 && !entity.orgnr) {
      return { ...entity, hasDuplicateEmails: true };
    }
    return entity;
  });

  return newEntities;
}
function allCases(string) {
  const upper = /[A-Z]/.test(string),
    lower = /[a-z]/.test(string);

  return upper && lower;
}
function preProcessInputData(inputData, existingEntities) {
  const existingEntitiesWithDuplicateFlags = markDuplicateNameEntities(
    markDuplicateEmailEntities(existingEntities)
  );
  const existingEntitiesCopy = [...existingEntitiesWithDuplicateFlags];
  const existingEntitiesMap = existingEntitiesCopy.reduce((acc, entity) => {
    if (entity.orgnr) {
      acc[entity.orgnr] = entity;
      return acc;
    }
    entity.firstName =
      entity.firstName || (entity.users && entity.users[0] && entity.users[0].firstName);
    entity.lastName =
      entity.lastName || (entity.users && entity.users[0] && entity.users[0].lastName);
    if (entity.firstName && entity.lastName) {
      const name =
        (entity.firstName || '').toLowerCase().trim() +
        ' ' +
        (entity.lastName || '').toLowerCase().trim();
      acc[name] = entity;
    }
    if ((entity && entity.users && entity.users[0] && entity.users[0].email) || entity.email) {
      acc[
        (entity && entity.users && entity.users[0] && entity.users[0].email.toLowerCase().trim()) ||
          entity.email.toLocaleLowerCase().trim()
      ] = entity;
    }
    if (entity.dob && isCorrectDateFormat(entity.dob)) {
      acc[generateDateOfBirth(entity)] = entity;
    }

    return acc;
  }, {});
  const newEntitiesWithDuplicateFlags = markDuplicateNameEntities(
    markDuplicateEmailEntities(
      inputData.map((x) => {
        if ('Fultnavn' in x) {
          let names = x['Fultnavn'].split(' ');
          x.firstname = names.slice(0, names.length - 1).join(' ');
          x.lastname = names[names.length - 1];
        }

        return {
          ...x,
          lastName: capitalizeCompany(x.lastname || ''),
          firstName: capitalizeCompany(x.firstname || ''),
        };
      })
    )
  );

  const data = [];
  for (const item of newEntitiesWithDuplicateFlags) {
    const newItem = {
      ...item,
      _id: uuid(),
      isCompany: !!item.companyname,
    };
    delete (newItem as any).firstname;
    delete (newItem as any).lastname;

    if (newItem.isCompany) {
      if (!allCases(newItem.companyname)) {
        newItem.companyname = capitalizeCompany(item.companyname || '');
      }
      newItem.orgnr = item.orgnr;
    } else {
      if (newItem.dob) {
        newItem.dob = generateDateOfBirth(newItem);
      }
    }

    const existingEntity = doEntitiesMatch(existingEntitiesMap, newItem);

    if (existingEntity) {
      const temp = { ...existingEntity };
      if (newItem.email && !existingEntity.email) {
        temp.email = newItem.email;
        existingEntity.email = newItem.email;
      }
      if (newItem.pnr && !existingEntity.pnr) {
        temp.pnr = newItem.pnr;
        existingEntity.pnr = newItem.pnr;
      }
      if (newItem.address && !existingEntity.address) {
        temp.address = newItem.address;
        existingEntity.address = newItem.address;
      }
      if (newItem.dob && !existingEntity.dob) {
        temp.dob = newItem.dob;
        existingEntity.dob = newItem.dob;
      }
      if (newItem.banknr && !existingEntity.banknr) {
        temp.banknr = newItem.banknr;
        existingEntity.banknr = newItem.banknr;
      }
      if (newItem.zip && !existingEntity.zip) {
        temp.zip = newItem.zip;
        existingEntity.zip = newItem.zip;
      }
      if (newItem.city && !existingEntity.city) {
        temp.city = newItem.city;
        existingEntity.city = newItem.city;
      }
      if (newItem.tlf && !existingEntity.tlf) {
        temp.tlf = newItem.tlf;
        existingEntity.tlf = newItem.tlf;
      }

      if ((newItem as any).contractTitle) {
        const contractitem = newItem as any;
        if (contractitem.additionalContacts) {
          if (contractitem.additionalContacts.length > 0) {
            delete contractitem.additionalContacts;
          }
        }
      }

      data.push({
        ...temp,
        records: (newItem as any).records,
        additionalContacts: (newItem as any).additionalContacts,
        additionalContracts: (newItem as any).additionalContracts,
      });
    } else {
      data.push({ ...newItem, createMe: true });
    }
  }

  //let mergedInputData: any[] = mergeInputData(inputData);
  return [data, existingEntitiesWithDuplicateFlags];
}

function extractRecords(inputData, shareclassMap) {
  let lst = [];

  for (const x of inputData) {
    const rec = x.records.map((record) => ({
      amount: record._amount,
      notes: record._notes,
      shareclassId: shareclassMap[record._shareclass.toLowerCase()]._id,
      entityId: x._id,
    }));

    lst = lst.concat(rec);
  }
  return lst;
}

class MissingAdditionalContactsNameException extends Error {
  public override name: string;
  constructor() {
    super('MissingAdditionalContactsName');
    this.name = 'MissingAdditionalContactsNameException';
  }
}

function extractAdditionalContacts(inputData) {
  return inputData.reduce((acc, entity) => {
    if (!entity.additionalContacts) {
      return acc;
    }
    acc[entity._id] = entity.additionalContacts
      .map((contact) => {
        const newContact = {
          ...contact,
          firstName: contact.firstName || contact.firstname,
          lastName: contact.lastName || contact.lastname,
        };

        delete newContact.lastname;
        delete newContact.firstname;
        delete newContact.isAdditional;
        delete newContact.type;
        if (newContact.dob) {
          delete newContact.dob;
        }

        return newContact;
      })
      .filter((c) => c.name || c.firstName || c.lastName || c.email);

    return acc;
  }, {});
}

function processEntities(inputData) {
  return inputData.map((entity) => {
    delete entity.additionalContacts;
    delete entity.records;
    delete entity.type;
    return entity;
  });
}

export function buildShareholderOnboardPayload(
  data: GenericData[],
  existingEntities: Entity[],
  existingShareClasses: ShareClass[]
): { payload: ShareholderOnboardPayload; metaData: any } {
  const shareclassMap: { [name: string]: ShareClass } = existingShareClasses.reduce(
    (acc, existingClass) => {
      acc[existingClass.name.toLowerCase()] = existingClass;
      return acc;
    },
    {}
  );

  const [preProcessedInputData, existingEntitiesWithDuplicateFlags]: any[][] = preProcessInputData(
    data,
    existingEntities
  );

  const metaData: any = {
    newClasses: 0,
    newOwners: preProcessedInputData.filter((entity) => entity.createMe).length,
    existingOwners: preProcessedInputData.filter((entity) => !entity.createMe).length,
  };

  const additionalContacts: { [key: string]: any } =
    extractAdditionalContacts(preProcessedInputData);

  for (const item of preProcessedInputData) {
    const shareclassnames = item.records.map((record) => record._shareclass);

    for (const shareclassname of shareclassnames) {
      const shareclass = shareclassMap[shareclassname.toLowerCase()];

      if (!shareclass) {
        metaData.newClasses++;
        const newClass = new ShareClass({
          _id: uuid(),
          name: shareclassname,
        });

        shareclassMap[shareclassname.toLowerCase()] = newClass;
      }
    }
  }
  const records = extractRecords(preProcessedInputData, shareclassMap);

  const shareclasses = Object.keys(shareclassMap).map((key) => shareclassMap[key]);

  const entities = processEntities(preProcessedInputData);

  const recordSet = new Set(records.map((record) => record.entityId));

  metaData.existingButNotInSpreadSheet = existingEntitiesWithDuplicateFlags.filter(
    (entity) => !recordSet.has(entity._id)
  );

  const state = { records };
  return {
    payload: {
      entities,
      state,
      shareclasses,
      additionalContacts,
      option: null,
    },
    metaData,
  };
}

function extractContracts(inputData, entities) {
  const payload = [];

  const entityMap = entities.reduce((acc, entity) => {
    acc[entity._id] = entity;
    return acc;
  }, {});

  for (const item of inputData) {
    if (item.additionalContracts) {
      for (const contract of item.additionalContracts) {
        const contractTranslatedType: string = Object.keys(translatedType).reduce(
          (contractType: string, key: string) => {
            if (
              translatedType[key].find(
                (ty: string) => ty === contract.contractType.toLocaleLowerCase()
              )
            ) {
              return key;
            }
            return contractType;
          },
          null
        );

        let contractTranslatedStatus;
        if (contract.contractStatus) {
          contractTranslatedStatus = Object.keys(translatedStatus).reduce(
            (contractStatus: string, key: string) => {
              if (
                translatedStatus[key].find(
                  (st: string) => st === contract.contractStatus.toLowerCase()
                )
              ) {
                return key;
              }
              return contractStatus;
            },
            null
          );
        } else {
          contractTranslatedStatus = 'not_exercised';
        }
        if (!contractTranslatedType) throw new InvalidContractType();
        if (!contractTranslatedStatus) throw new InvalidContractStatus();
        //stockcontracttypeOPTION = 'OPTION',
        //WARRANT = 'WARRANT',
        //CONVERTIBLE = 'CONVERTIBLE',
        let contractType = StockContractType.OPTION;
        switch (contractTranslatedType.toUpperCase()) {
          case 'OPTION':
            contractType = StockContractType.OPTION;
            break;
          case 'WARRANT':
            contractType = StockContractType.WARRANT;
            break;
          case 'CONVERTIBLE':
            contractType = StockContractType.CONVERTIBLE;
            break;
          default:
            contractType = StockContractType.OPTION;
        }

        let contractStatus: ContractStatus;
        switch (contractTranslatedStatus.toUpperCase()) {
          case 'NOT_EXERCISED':
            contractStatus = ContractStatus.NOT_EXERCISED;
            break;
          case 'CANCELLED':
            contractStatus = ContractStatus.CANCELLED;
            break;
          case 'EXPIRED':
            contractStatus = ContractStatus.EXPIRED;
            break;
          case 'EXERCISED':
            contractStatus = ContractStatus.EXERCISED;
            break;
        }
        if (!contract.contractExpiryDate) {
          contract.contractExpiryDate = null;
        }
        payload.push({
          _id: uuid(),
          title: contract.contractTitle,
          amount: contract.contractedAmount,
          entity: entityMap[item._id],
          shareclass: contract._shareclass,
          type: contractType,
          strike: contract?.strikePrice,
          awaitingConditions: false,
          size: contract?.totalLoan,
          convertiblePart: contract?.convertibleShareOfLoan,
          interestPerYear: contract?.effectiveInterestRate,
          paymentStructure: contract.contractPaymentStructure,
          conditions: contract?.contractTerms,
          status: contractStatus,
          expirationDate: contract?.contractExpiryDate
            ? new Date(contract.contractExpiryDate)
            : null,
        });
      }
    }
  }

  return payload;
}
function isContractexisitng(contract, contracts: StockContract[]) {
  for (const item of contracts) {
    if (
      item.title.trim() === contract.title.trim() &&
      item.shareclass.name === contract.shareclass
    ) {
      return item._id;
    }
  }

  return null;
}
export function buildContractsAndShareholdersOnboardPayload(
  data: GenericData[],
  existingEntities: Entity[],
  existingShareClasses: ShareClass[],
  exisitngContracts: StockContract[]
): { payload: ShareholderOnboardPayload; metaData: any } {
  //parse the payload to find all contracts for each entity found in the payload
  //then add the contracts to the payload
  const shareclassMap: { [name: string]: ShareClass } = existingShareClasses.reduce(
    (acc, existingClass) => {
      acc[existingClass.name] = existingClass;
      return acc;
    },
    {}
  );

  const [preProcessedInputData, existingEntitiesWithDuplicateFlags]: any[][] = preProcessInputData(
    data,
    existingEntities
  );

  const metaData: any = {
    newClasses: 0,
    newOwners: preProcessedInputData.filter((entity) => entity.createMe).length,
    existingOwners: preProcessedInputData.filter((entity) => !entity.createMe).length,
  };

  const additionalContacts: { [key: string]: any } =
    extractAdditionalContacts(preProcessedInputData);

  for (const item of preProcessedInputData) {
    const shareclassnames = item.records.map((record) => record._shareclass);

    for (const shareclassname of shareclassnames) {
      const shareclass = shareclassMap[shareclassname.toLowerCase()];

      if (!shareclass) {
        metaData.newClasses++;
        const newClass = new ShareClass({
          _id: uuid(),
          name: shareclassname,
        });

        shareclassMap[shareclassname.toLowerCase()] = newClass;
      }
    }
  }

  const shareclasses = Object.keys(shareclassMap).map((key) => shareclassMap[key]);

  const records = extractRecords(preProcessedInputData, shareclassMap);

  const recordSet = new Set(records.map((record) => record.entityId));
  metaData.existingButNotInSpreadSheet = existingEntitiesWithDuplicateFlags.filter(
    (entity) => !recordSet.has(entity._id)
  );

  const entities = processEntities(preProcessedInputData);
  const contracts = extractContracts(preProcessedInputData, entities);
  contracts.forEach((contract) => {
    contract._id = isContractexisitng(contract, exisitngContracts);
  });
  const state = { records };
  return {
    payload: {
      entities,
      state,
      shareclasses,
      additionalContacts,
      contracts,
      option: null,
    },
    metaData,
  };
}

class MissingCompanyNameException extends Error {
  public override name: string;
  constructor() {
    super('MissingCompanyName');
    this.name = 'MissingCompanyNameException';
  }
}

class MissingOrgnumberException extends Error {
  public override name: string;
  constructor() {
    super('MissingOrgNumber');
    this.name = 'MissingOrgnumberException';
  }
}

function companyCheck(inputData) {
  for (const row of inputData) {
    if (row.companyname && !row.orgnr) {
      console.log('Company Name: ', row.companyname);
      throw new MissingOrgnumberException();
    } else if (row.orgnr && !row.companyname) {
      console.log('Org Nr: ', row.orgnr);

      throw new MissingCompanyNameException();
    }
  }
}

class MissingIdentificationException extends Error {
  public override name: string;
  constructor() {
    super('MissingIdentification');
    this.name = 'MissingIdentification';
  }
}
class InvalidContractType extends Error {
  public override name: string;
  constructor() {
    super('InvalidContractType');
    this.name = 'InvalidContractType';
  }
}
class InvalidContractStatus extends Error {
  public override name: string;
  constructor() {
    super('InvalidContractStatus');
    this.name = 'InvalidContractStatus';
  }
}

function idCheck(inputData) {
  let withoutId = 0;
  for (const row of inputData) {
    if (!row.companyname && !row.orgnr && !row.email && !row.lastname && !row.firstname) {
      // throw new MissingIdentificationException();
      row.companyname = 'Unknown' + withoutId;
      row.orgnr = withoutId;
      withoutId++;
    }
  }
}

export function scanAndChangeOrgDob(data: GenericData[]): GenericData[] {
  const countOrgDob = data.filter((data) => data.orgnrdob).length;
  if (countOrgDob > 0) {
    for (const row of data) {
      //strip the - , . and spaces from the orgnrdob to test the length
      if (!row.orgnrdob) {
        if (!row.orgnr && !row.pnr && !row.dob) {
          console.log('Error row:', row);
          throw new MissingOrgnumberException();
        }
        continue;
      }

      const orgnrdob = row.orgnrdob.replace(/[-.,/ ]+/g, '');
      switch (orgnrdob.length) {
        case OrgNrDobLenght.DOBANDPNR:
          //use moment to check if the date is valid
          if (isCorrectDateFormat(orgnrdob.substring(0, 6))) {
            row.dob = (row.orgnrdob + '').substring(0, 6);
            row.pnr = (row.orgnrdob + '').substr(-5);
          } else {
            row.orgnr = orgnrdob;
          }
          break;
        case OrgNrDobLenght.DOB:
        case OrgNrDobLenght.DOBYEAR:
          //use moment to check if the date is valid
          if (isCorrectDateFormat(orgnrdob)) {
            row.dob = orgnrdob;
          } else {
            row.orgnr = orgnrdob;
          }
          break;
        case OrgNrDobLenght.ORGNR:
          row.orgnr = row.orgnrdob;
          if (!row.companyname) {
            row.companyname = row['name'];
          }
          break;
        case OrgNrDobLenght.EMPTY:
          break;
        default:
          if (row.pnr) {
            if (!row.dob && row.pnr.length === OrgNrDobLenght.DOBANDPNR) {
              row.dob = (row.pnr + '').substring(0, 6);
            }
            break;
          }
          if (!row.companyname) {
            row.companyname = row['name'];
          }
          row.orgnr = row.orgnrdob;
          break;
      }
      delete row.orgnrdob;
    }
  }
  return data;
}

export function scanAndChangeFullName(data: GenericData[]): GenericData[] {
  for (const row of data) {
    if (row.name) {
      try {
        let names: string[], firstname: string, lastname: string;

        if (row.name.indexOf(',') !== -1) {
          names = row.name.trim().split(',');
          firstname = names[1];
          lastname = names[0];
        } else {
          names = row.name.trim().split(' ');
          firstname = names.slice(0, -1).join(' ');
          lastname = names[names.length - 1];
        }

        if (row.firstname) {
          row.lastname = row.name;
        } else {
          row.firstname = firstname;
          row.lastname = lastname;
        }

        delete row.name;
      } catch (error: any) {
        row.firstname = row.name;
      }
    }
  }
  return data;
}

// When for instance someone is uploading a FLI spreadsheet without amount
function setAmountIfNoneHaveIt(data: GenericData[]): GenericData[] {
  if (data.find((point) => point._amount)) {
    return data;
  } else {
    return data.map((point) => {
      return { ...point, _amount: 1 };
    });
  }
}

export function parseTaxAddress(data: GenericData[]): GenericData[] {
  const lst = [];
  for (const gen of data) {
    let newGen: GenericData;
    try {
      const str = gen.address.split(' ');
      const postnr = str[0];
      const city = str[1];
      newGen = { ...gen, zip: postnr, city, address: null };
    } catch (err: any) {
      newGen = { ...gen };
    }
    lst.push(newGen);
  }
  return lst;
}

export function setEmptyShareclassToDefault(orgType: string, data: GenericData[]): GenericData[] {
  return data.map((gen: GenericData) => {
    if (gen._shareclass) {
      return gen;
    } else {
      return {
        ...gen,
        _shareclass: defaultShareClass[orgType] || defaultShareClass['AS'],
      };
    }
  });
}

export function shareholderImportPipeline(
  data: GenericData[],
  existingEntities: Entity[],
  existingShareClasses: ShareClass[],
  currentOrg: any
): { payload: ShareholderOnboardPayload; metaData: any } {
  const orgtype = currentOrg.type || 'AS';

  let output: GenericData[] = scanAndChangeOrgDob(data);

  output = scanAndChangeFullName(output);

  output = setEmptyShareclassToDefault(orgtype, output);
  output = setAmountIfNoneHaveIt(output);
  output = fillEmptyRows(output);

  const res: any = mergeDuplicateEntityAndShareclassRows(output);

  //find out if there is any shacreclasses in the data

  let payload = createAdditionalContactsField(res.payload);
  payload = removeAdditionalContactsRows(payload);

  companyCheck(payload);

  idCheck(payload);

  payload = createShareclassRecordsField(payload, orgtype);
  const result = buildShareholderOnboardPayload(payload, existingEntities, existingShareClasses);

  if (res.duplicateRows.length > 0) {
    result.metaData.duplicateRows = res.duplicateRows;
  }

  return result;
}

export function importContractsPipeline(
  data: GenericData[],
  existingEntities: Entity[],
  existingShareClasses: ShareClass[],
  exisitngContracts: StockContract[],
  currentOrg: any
): { payload: any; metaData: any } {
  const orgtype = currentOrg.type || 'AS';
  let output: GenericData[] = scanAndChangeOrgDob(data);

  output = scanAndChangeFullName(output);

  output = setEmptyShareclassToDefault(orgtype, output);
  output = setAmountIfNoneHaveIt(output);
  output = fillEmptyRows(output);

  //output = fillContractRows(output);
  const res: any = mergeDuplicateEntityAndShareclassRows(output);
  console.log(res);
  //find out if there is any shacreclasses in the data

  let payload = createAdditionalContactsField(res.payload);
  //find contract rows?
  console.log(payload);

  payload = createContractsField(payload);

  payload = removeAdditionalContactsRows(payload);
  payload = removeContractRows(payload);
  companyCheck(payload);

  idCheck(payload);
  payload = createShareclassRecordsField(payload, orgtype);

  const result = buildContractsAndShareholdersOnboardPayload(
    payload,
    existingEntities,
    existingShareClasses,
    exisitngContracts
  );
  if (res.duplicateRows.length > 0) {
    result.metaData.duplicateRows = res.duplicateRows;
  }
  console.log('result', result);
  return result;
}
